import * as Accordion from "#root/components/accordion";
import { Case, Switch } from "#root/components/conditionals";
import * as Fields from "#root/components/form/fields";
import { GlyphLoading } from "#root/components/glyphs";
import Sidebar from "#root/components/sidebar";
import StandardEditor from "#root/components/standard-editor";
import { useData } from "#root/utility/hooks";
import { mergeUpdate } from "#root/utility/merge-update";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import { flatToNamedTree } from "#root/utility/tree";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getNewsRPMInstanceFiltersCollection, type NewsRPMInstanceFilterListItem } from "#root/schema/platform/newsrpm-instance";
import PageLayout from "./layout";

export default function ({ instanceId, filterId }: { instanceId: string; filterId: string | null }): ReactNode {
  const navigate = useNavigate();
  const filterCollection = useMemo(() => getNewsRPMInstanceFiltersCollection(instanceId), [instanceId]);

  const [filters, setFilters] = useData(async () => {
    const { rows } = await filterCollection.list();
    return rows;
  }, [filterCollection, instanceId]);

  const handleUpdate = useCallback(
    (data: RecordModificationResponse<NewsRPMInstanceFilterListItem>) => {
      setFilters(mergeUpdate(filters!, data));

      if ("deleted" in data) {
        navigate(`/admin/platform/newsrpm-instance/${instanceId}/filter`);
      } else if ("created" in data) {
        navigate(`/admin/platform/newsrpm-instance/${instanceId}/filter/${data.created}`);
      }
    },
    [navigate, setFilters, instanceId, filters],
  );

  if (!filters) {
    return (
      <PageLayout title="Filter">
        <section>
          <GlyphLoading/>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Admin - Filter">
      <FilterSidebar instanceId={instanceId} filters={filters} id={filterId}/>
      <section>
        <Switch>
          <Case expr={filterId === "new"}>
            <h1>New Filter</h1>
            <FilterEditor collection={filterCollection} onSave={handleUpdate}/>
          </Case>
          <Case expr={!!filterId}>
            <h1>Edit Filter</h1>
            <FilterEditor id={filterId} collection={filterCollection} onSave={handleUpdate} onDelete={handleUpdate}/>
          </Case>
        </Switch>
      </section>
    </PageLayout>
  );
}

function FilterSidebar({ instanceId, filters, id }: { instanceId: string; filters: NewsRPMInstanceFilterListItem[]; id: string | null }) {
  const navigate = useNavigate();
  const categories = useMemo(() => flatToNamedTree(filters, f => f.field, f => f.value), [filters]);

  return (
    <Sidebar>
      <button className="flat center" type="button" onClick={() => navigate(`/admin/platform/newsrpm-instance/${instanceId}/filter/new`)}>
        New Filter
      </button>
      <Accordion.Parent initialItemId={id}>
        {categories.map(({ name, items }) => (
          <Accordion.Group key={name} label={`Field: ${name}`}>
            {items.map(item => (
              <Accordion.Item key={item.id} itemId={item.id}>
                <Link to={`/admin/platform/newsrpm-instance/${instanceId}/filter/${item.id}`}>
                  {item.field}: {item.value}
                </Link>
              </Accordion.Item>
            ))}
          </Accordion.Group>
        ))}
      </Accordion.Parent>
    </Sidebar>
  );
}

function FilterEditor(props) {
  return (
    <StandardEditor {...props}>
      <Fields.Set name="field">
        <label>Field</label>
        <Fields.Select>
          <Fields.Option value="publisher">publisher</Fields.Option>
          <Fields.Option value="providerCode">providerCode</Fields.Option>
          <Fields.Option value="topic">topic</Fields.Option>
          <Fields.Option value="feature">feature</Fields.Option>
        </Fields.Select>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="value">
        <label>Value</label>
        <Fields.String/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="editorialAction">
        <label>Editorial Action</label>
        <Fields.Select>
          <Fields.Option value="none">Do Nothing</Fields.Option>
          <Fields.Option value="drop">Drop Article</Fields.Option>
          <Fields.Option value="sponsored">Flag as Sponsored</Fields.Option>
        </Fields.Select>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="qualityAdjustment">
        <label>Quality Adjustment (# of hours +/- to prioritize)</label>
        <Fields.Number/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="library">
        <label>Add Article to Library</label>
        <Fields.GenericName/>
        <Fields.Validity/>
      </Fields.Set>
    </StandardEditor>
  );
}
